namespace $ {

	export function $shm_projects_codex_project_scope(
		records: readonly $shm_projects_data_record[],
		root: string,
	) {
		const result: string[] = []
		const pending = [ root ]
		const seen = new Set< string >()
		while( pending.length ) {
			const id = pending.shift()!
			if( seen.has( id ) ) continue
			seen.add( id )
			result.push( id )
			pending.push( ... records.filter( project => project.parent_id === id ).map( project => project.id ) )
		}
		return result
	}

	export function $shm_projects_codex_session_owner( name: string, projects: readonly string[] ) {
		for( const project_id of projects ) {
			for( const kind of [ 'project-session', 'project' ] as const ) {
				const prefix = `[${ kind }:${ project_id }] `
				if( name.startsWith( prefix ) ) return { project_id, prefix, consultation: kind === 'project' }
			}
		}
		return null
	}

}

namespace $.$$ {
	export class $shm_projects_codex_chat extends $.$shm_projects_codex_chat {

		@ $mol_mem
		override Sessions_head() {
			const head = super.Sessions_head()
			head.close_content = ()=> [ this.Sessions_actions() ]
			return head
		}

		@ $mol_action
		sessions_unpin( event?: Event | null ) {
			if( !event ) return null
			this.sessions_unpin_all( event )
			this.Sessions_actions().showed( false )
			return null
		}

		server_url( next?: string ) {
			const site = this.$.$shm_projects_config.site_api_url()
			return site ? `${ site }codex-api` : super.server_url()
		}

		codex_server_restart_visible() {
			const url = new URL( this.server_url() )
			return [ '127.0.0.1', 'localhost', '[::1]' ].includes( url.hostname )
				&& url.port === '4870'
		}

		agent_sessions_settings_after_limits() {
			return this.codex_server_restart_visible()
				? [ this.Sessions_memory(), this.Sessions_restart() ]
				: []
		}

		@ $mol_mem
		codex_server_status() {
			const server = this.server_url()
			const stamp = Math.floor( this.$.$mol_state_time.now( 2_000 ) / 2_000 )
			return this.$.$shm_wire_latest(
				`${ server }/status`,
				()=> $mol_wire_sync( $shm_codex_api ).status( server, stamp ),
			)
		}

		codex_server_memory() {
			return this.$.$mol_error_fence(
				()=> {
					const memory = this.codex_server_status().memory
					const mib = ( bytes: number )=> Math.round( bytes / 1024 / 1024 ).toLocaleString( 'ru-RU' )
					return `Heap: ${ mib( memory.heapUsed ) } из ${ mib( memory.heapTotal ) } МБ · RSS: ${ mib( memory.rss ) } МБ`
				},
				()=> 'Недоступно',
				()=> 'Загрузка…',
			)
		}

		@ $mol_action
		override codex_server_restart( next?: Event ) {
			if( next === undefined ) return null
			this.$.$shm_codex_api.restart( this.server_url() ).catch( $mol_fail_log )
			return next
		}

		agent_defaults_namespace() {
			return `${ super.agent_defaults_namespace() }:${ this.project_id() }`
		}

		project_scope_ids() {
			return $shm_projects_codex_project_scope( this.$.$shm_projects_data.records(), this.project_id() )
		}

		project_session_prefixes() {
			return this.project_scope_ids().flatMap( project_id => [
				`[project-session:${ project_id }] `,
				`[project:${ project_id }] `,
			] )
		}

		events_prefixes() {
			return this.project_session_prefixes().join( '\n' )
		}

		session_owner( session: Pick< $shm_codex_api_thread, 'name' > ) {
			return $shm_projects_codex_session_owner( session.name ?? '', this.project_scope_ids() )
		}

		session_prefix() {
			return `[project-session:${ this.project_id() }] `
		}

		consultation_prefix() {
			return `[project:${ this.project_id() }] `
		}

		agent_sessions_load( refresh = 0 ) {
			const prefixes = this.project_session_prefixes().join( '\n' )
			return $mol_wire_sync( $shm_codex_api ).sessions( this.server_url(), refresh, prefixes )
		}

		session_project_owned( session: $shm_codex_api_thread ) {
			return Boolean( this.session_owner( session ) )
		}

		session_visible( session: $shm_codex_api_thread ) {
			return this.session_project_owned( session )
		}

		event_visible( event: any ) {
			const thread = event.params?.threadId || event.params?.thread?.id
			if( !thread ) return true
			if( event.params?.thread ) return this.session_visible( event.params.thread )
			if( event.method === 'thread/name/updated' && this.session_owner({ name: event.params?.name })) return true
			return this.$.$mol_error_fence(
				()=> Boolean( this.session_by_id( thread ) ),
				()=> false,
				()=> false,
			)
		}

		agent_event_handle( event: any ) {
			if( event.method !== 'thread/name/updated' ) return
			const id = String( event.params?.threadId || '' )
			const name = String( event.params?.name || '' )
			if( !id || !this.session_owner({ name }) || this.session_by_id( id ) ) return
			this.session_upsert({ id, name })
		}

		override session_title( id: string ) {
			const session = this.session_by_id( id )
			const title = session?.name || session?.preview || `Сессия ${ id.slice( 0, 8 ) }`
			const owner = this.session_owner({ name: title })
			return owner ? title.slice( owner.prefix.length ) : title
		}

		@ $mol_mem_key
		session_title_draft( id: string, next?: string ) {
			$mol_wire_solid()
			const title = next ?? this.session_title( id )
			const owner = this.session_owner({ name: title })
			return owner ? title.slice( owner.prefix.length ) : title
		}

		agent_session_rename( id: string, name: string ) {
			if( this.session_owner({ name }) ) {
				return $mol_wire_sync( $shm_codex_api ).rename( this.server_url(), id, name )
			}
			const current = this.session_by_id( id )
			const prefix = current ? this.session_owner( current )?.prefix ?? this.session_prefix() : this.session_prefix()
			return $mol_wire_sync( $shm_codex_api ).rename( this.server_url(), id, `${ prefix }${ name }` )
		}

		session_tags( id: string ) {
			const session = this.session_by_id( id )
			const owner = session ? this.session_owner( session ) : null
			const tags = [ ...( session?.tags ?? [] ) ]
			if( owner?.consultation ) tags.unshift( 'консультация' )
			if( owner ) tags.unshift( owner.project_id )
			return [ ... new Set( tags ) ]
		}

		all_tags() {
			const sessions = ( this as any ).sessions_data() as $shm_codex_api_thread[]
			return [ ... new Set< string >( sessions.flatMap( session => this.session_tags( session.id ) ) ) ]
				.sort( ( left, right )=> left.localeCompare( right ) )
		}

		session_create_instructions() {
			return ''
		}

		async session_create_context_async() {
			const context = await $shm_projects_goal_api.session_context( this.project_id() )
			return { cwd: context.cwd, instructions: '' }
		}

		app_pages() {
			const pages = super.app_pages()
			return this.session_id() ? pages : [ this.Sessions_page() ]
		}

		sessions_title() {
			return 'Сессии'
		}

		@ $mol_mem
		override session_id( next?: string ) {
			return this.$.$mol_state_arg.value( 'project_session', next === undefined ? undefined : next || null ) ?? ''
		}

		session_arg( id: string ) {
			return { project_session: id }
		}

	}

	export class $shm_projects_codex extends $.$shm_projects_codex {

		project_settings_content() {
			return [ this.Codex().Sessions_settings_content() ]
		}

		@ $mol_mem
		project_brought( next?: string ) {
			return next ?? ''
		}

		@ $mol_action
		project_bring_ensure() {
			const project = this.project_selected()
			if( !project || this.project_brought() === project ) return null
			this.project_brought( project )
			new this.$.$mol_after_tick( ()=> {
				if( this.project_selected() === project ) this.project_bring()
			} )
			return null
		}

		project_bring() {
			new this.$.$mol_after_tick( ()=> {
				const book = this.dom_node() as HTMLElement
				const page = this.Codex().Sessions_page().dom_node() as HTMLElement
				book.scroll({
					left: page.offsetLeft + page.offsetWidth - book.offsetWidth,
					behavior: 'smooth',
				})
			} )
		}

		catalog_pages() {
			const project = this.project_selected()
			this.project_bring_ensure()
			return [
				this.Menu_page(),
				...( project ? this.Codex().pages_deep() : [] ),
			]
		}

	}

}
